/**
 * Org Service
 *
 * Wraps org-related API endpoints for multi-tenant / agency mode.
 * Fetches the authenticated user's orgs, their clients, and members.
 *
 * Tokens are never returned by the API; this only fetches metadata.
 */

import { api } from './api';

/**
 * Fetch all organizations the current user belongs to.
 * Returns an array of org objects:
 *   { id, slug, name, member_role, ... }
 */
export async function getUserOrgs() {
  const res = await api.get('/orgs/');
  return res.data?.orgs ?? res.data ?? [];
}

/**
 * Fetch all clients under a specific organization.
 * Returns an array of client objects:
 *   { id, name, external_id, ... }
 */
export async function getOrgClients(orgId) {
  const res = await api.get(`/orgs/${orgId}/clients`);
  return res.data?.clients ?? res.data ?? [];
}

/**
 * Fetch all members of a specific organization.
 * Returns an array of member objects:
 *   { id, email, role, ... }
 */
export async function getOrgMembers(orgId) {
  const res = await api.get(`/orgs/${orgId}/members`);
  return res.data?.members ?? res.data ?? [];
}

/**
 * Create a new client under an organization.
 */
export async function createClient(orgId, { name, description, website }) {
  const res = await api.post(`/orgs/${orgId}/clients`, { name, description, website });
  return res.data;
}

/**
 * Update an existing client (name, description, website, status).
 */
export async function updateClient(orgId, clientId, updates) {
  const res = await api.patch(`/orgs/${orgId}/clients/${clientId}`, updates);
  return res.data;
}

/**
 * Archive a client (soft delete).
 */
export async function archiveClient(orgId, clientId) {
  const res = await api.delete(`/orgs/${orgId}/clients/${clientId}`);
  return res.data;
}

/**
 * Get a single client's detail.
 */
export async function getClient(orgId, clientId) {
  const res = await api.get(`/orgs/${orgId}/clients/${clientId}`);
  return res.data;
}

/**
 * List connected accounts linked to a client.
 */
export async function getClientAccounts(orgId, clientId) {
  const res = await api.get(`/orgs/${orgId}/clients/${clientId}/accounts`);
  return res.data?.accounts ?? res.data ?? [];
}

/**
 * Link a channel account to a client.
 */
export async function linkClientAccount(orgId, clientId, { channel_type, channel_account_id, channel_account_name }) {
  const res = await api.post(`/orgs/${orgId}/clients/${clientId}/accounts`, {
    channel_type, channel_account_id, channel_account_name,
  });
  return res.data;
}

/**
 * Unlink a channel account from a client.
 */
export async function unlinkClientAccount(orgId, clientId, accountId) {
  return api.delete(`/orgs/${orgId}/clients/${clientId}/accounts/${accountId}`);
}

/**
 * Fetch client overview with campaigns and metrics for all clients in an org.
 */
export async function getClientOverview(orgId, lookbackDays = 14) {
  const res = await api.get(`/orgs/${orgId}/client-overview`, { params: { lookback_days: lookbackDays } });
  return res.data;
}

/**
 * Update a unified campaign (status, name, objective).
 */
export async function updateCampaign(orgId, campaignUuid, updates) {
  const res = await api.patch(`/orgs/${orgId}/campaigns/${campaignUuid}`, updates);
  return res.data;
}

// ── White-Label Reports ──────────────────────────────────────────────────────

/**
 * Generate a branded white-label report for a client.
 */
export async function generateWhitelabelReport(orgId, { client_id, period_days, provider_scope, title, expires_days }) {
  const res = await api.post(`/orgs/${orgId}/reports/generate`, {
    client_id, period_days, provider_scope, title, expires_days,
  });
  return res.data;
}

/**
 * List all generated white-label reports for an org.
 */
export async function listWhitelabelReports(orgId) {
  const res = await api.get(`/orgs/${orgId}/reports`);
  return res.data;
}

/**
 * Create a recurring report schedule.
 */
export async function createReportSchedule(orgId, schedule) {
  const res = await api.post(`/orgs/${orgId}/reports/schedule`, schedule);
  return res.data;
}

/**
 * List report schedules for an org.
 */
export async function listReportSchedules(orgId) {
  const res = await api.get(`/orgs/${orgId}/reports/schedules`);
  return res.data;
}

/**
 * Update a report schedule.
 */
export async function updateReportSchedule(orgId, scheduleId, updates) {
  const res = await api.patch(`/orgs/${orgId}/reports/schedules/${scheduleId}`, updates);
  return res.data;
}

/**
 * Delete a report schedule.
 */
export async function deleteReportSchedule(orgId, scheduleId) {
  return api.delete(`/orgs/${orgId}/reports/schedules/${scheduleId}`);
}

export default {
  getUserOrgs, getOrgClients, getOrgMembers,
  createClient, updateClient, archiveClient, getClient,
  getClientAccounts, linkClientAccount, unlinkClientAccount,
  getClientOverview, updateCampaign,
  generateWhitelabelReport, listWhitelabelReports,
  createReportSchedule, listReportSchedules, updateReportSchedule, deleteReportSchedule,
};
